<template>
  <b-modal v-model="showModal" title="Enter Auto Mode?" @ok="onConfirm" @cancel="onCancel" ok-title="Enter Auto Mode"
    cancel-title="Cancel" ok-variant="primary" centered>
    <p class="mb-3">
      <strong>Auto Mode</strong> will automatically navigate you to your next pending task based on queue priority.
    </p>

    <p class="mb-3">
      When you complete a task, the system will automatically fetch and navigate to the next item in your queue.
    </p>

    <div class="filter-grid">
      <div class="filter-item">
        <label>
          Working Queues
          <HelpPopup>
            Select specific queues to work, or leave empty for all queues
          </HelpPopup>
        </label>
        <multiselect
          v-model="selectedQueues"
          :options="availableQueues"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          placeholder="All Queues"
          :disabled="loadingQueues"
        />
      </div>

      <div class="filter-item">
        <label>
          Stores
          <HelpPopup>
            Filter by store. Items without store data (Allergy, Insurance) are always included.
          </HelpPopup>
        </label>
        <multiselect
          v-model="selectedStores"
          :options="storeOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          label="name"
          track-by="id"
          placeholder="All Stores"
          :disabled="loadingStores"
        />
      </div>

      <div class="filter-item">
        <label>
          Programs
          <HelpPopup>
            Filter by program. Ingestion only serves items in the selected programs' buckets. Other queues
            always include items without program data.
          </HelpPopup>
        </label>
        <multiselect
          v-model="selectedPrograms"
          :options="programOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          label="name"
          track-by="id"
          placeholder="All Programs"
          :disabled="loadingPrograms"
        />
      </div>
    </div>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import AutoModeService, { AutoModeFilterOption, AutoModeFilters } from '@/services/AutoModeService';
import HelpPopup from '../HelpPopup.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Store } from '@/models/Store';
import { Program } from '@/models/Program';

@Component({ components: { HelpPopup, Multiselect } })
export default class AutoModeConfirmModal extends Vue {
  @Prop({ type: Boolean, default: false }) readonly show!: boolean;

  private showModal: boolean = false;
  private selectedQueues: string[] = [];
  private availableQueues: string[] = [];
  private loadingQueues: boolean = false;

  private selectedStores: AutoModeFilterOption[] = [];
  private storeOptions: AutoModeFilterOption[] = [];
  private loadingStores: boolean = false;

  private selectedPrograms: AutoModeFilterOption[] = [];
  private programOptions: AutoModeFilterOption[] = [];
  private loadingPrograms: boolean = false;

  @Watch('show', { immediate: true })
  async onShowChanged(newVal: boolean): Promise<void> {
    this.showModal = newVal;

    if (newVal) {
      await Promise.all([
        this.loadAvailableQueues(),
        this.loadStores(),
        this.loadPrograms(),
      ]);
      this.loadSavedPreferences();
    }
  }

  @Watch('showModal')
  onShowModalChanged(newVal: boolean): void {
    if (!newVal && newVal !== this.show) {
      this.$emit('update:show', false);
    }
  }

  private async loadAvailableQueues(): Promise<void> {
    this.loadingQueues = true;
    try {
      this.availableQueues = await AutoModeService.getAvailableQueues();
    } catch (error) {
      console.error('Failed to load available queues:', error);
      this.$notification(NotificationOptions.error("Failed to load available queues"));
      this.availableQueues = ['Ingestion', 'Allergy', 'Online Order', 'Insurance', 'PV1', 'Consultation', 'Transfer', 'Pending Billing'];
    } finally {
      this.loadingQueues = false;
    }
  }

  private async loadStores(): Promise<void> {
    this.loadingStores = true;
    try {
      const response = await axios.get<Store[]>('/Store/', { params: { filterByTeam: true } });
      this.storeOptions = response.data
        .filter(s => s.active)
        .map(s => ({ id: s.id, name: s.name }));
    } catch (error) {
      console.error('Failed to load stores:', error);
    } finally {
      this.loadingStores = false;
    }
  }

  private async loadPrograms(): Promise<void> {
    this.loadingPrograms = true;
    try {
      const response = await axios.get<Program[]>('/api/Program');
      this.programOptions = response.data
        .filter((p: any) => p.active)
        .map((p: any) => ({ id: p.id, name: p.name }));
    } catch (error) {
      console.error('Failed to load programs:', error);
    } finally {
      this.loadingPrograms = false;
    }
  }

  private loadSavedPreferences(): void {
    // Queues
    const savedQueues = AutoModeService.getSavedSpecificQueues();
    this.selectedQueues = savedQueues.filter(q => this.availableQueues.includes(q));

    // Stores
    const savedStores = AutoModeService.getSavedStoreFilters();
    const storeIdSet = new Set(this.storeOptions.map(s => s.id));
    this.selectedStores = savedStores.filter(s => storeIdSet.has(s.id));

    // Programs
    const savedPrograms = AutoModeService.getSavedProgramFilters();
    const programIdSet = new Set(this.programOptions.map(p => p.id));
    this.selectedPrograms = savedPrograms.filter(p => programIdSet.has(p.id));
  }

  onConfirm(): void {
    AutoModeService.saveSpecificQueues(this.selectedQueues);
    AutoModeService.saveStoreFilters(this.selectedStores);
    AutoModeService.saveProgramFilters(this.selectedPrograms);

    const filters: AutoModeFilters = {
      queues: this.selectedQueues.length ? this.selectedQueues : undefined,
      storeIds: this.selectedStores.length ? this.selectedStores.map(s => s.id) : undefined,
      programIds: this.selectedPrograms.length ? this.selectedPrograms.map(p => p.id) : undefined,
    };

    this.$emit('confirmed', filters);
  }

  onCancel(): void {
    this.$emit('cancelled');
  }
}
</script>

<style scoped>
.filter-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-item label {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

::v-deep .modal-footer .btn-primary {
  background-color: #ff6b35;
  border-color: #ff6b35;
  font-weight: bold;
}

::v-deep .modal-footer .btn-primary:hover,
::v-deep .modal-footer .btn-primary:focus,
::v-deep .modal-footer .btn-primary:active {
  background-color: #e55d2d;
  border-color: #e55d2d;
}
</style>
